import { useMemo, useState } from 'react';
import { Loader2, Trash2, RefreshCw, Search, CheckCircle2, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useChannelProperties,
  usePropertyChannel,
  useCreateChannelConnection,
  useUpdateChannelConnection,
  useDeleteChannelConnection,
  useAddChannelMapping,
  useDeleteChannelMapping,
  useSyncChannelNow,
} from '@/hooks/api/useAdminChannels';
import { useAdminParityRollup } from '@/hooks/api/useChannelSync';
import type { AdminChannelProperty } from '@/services/api/adminChannels.api';

function fmtSyncedRelative(iso: string | null): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function parseRatePlanIds(raw: string): string[] {
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
}

const inputCls =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

export function AdminChannelsPage() {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const { data: properties, isLoading } = useChannelProperties();
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = properties ?? [];
    if (!q) return list;
    return list.filter(
      (p) => p.name.toLowerCase().includes(q) || p.clientName.toLowerCase().includes(q),
    );
  }, [properties, search]);

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900">Channels</h1>
      <p className="mt-1 text-sm text-gray-500">
        Set up AxisRooms for any property, across all clients — no property login needed.
      </p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* Property list */}
        <div className="rounded-xl border border-gray-200 bg-white">
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search hotels or clients..."
                aria-label="Search properties"
                className={cn(inputCls, 'pl-9')}
              />
            </div>
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center gap-2 p-4 text-sm text-gray-500">
                <Loader2 size={16} className="animate-spin" aria-hidden="true" /> Loading…
              </div>
            ) : filtered.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">No properties match.</p>
            ) : (
              <ul>
                {filtered.map((p) => (
                  <li key={p.propertyId}>
                    <button
                      type="button"
                      onClick={() => setSelected(p.propertyId)}
                      className={cn(
                        'w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 focus-visible:outline-none focus-visible:bg-gray-50',
                        selected === p.propertyId && 'bg-primary-50',
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">{p.name}</span>
                        <span
                          className={cn(
                            'shrink-0 inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium',
                            p.hasConnection ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500',
                          )}
                        >
                          {p.hasConnection ? 'Connected' : 'Not set up'}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">{p.clientName}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Detail panel */}
        <div>
          {selected ? (
            <PropertyChannelPanel propertyId={selected} />
          ) : (
            <div className="flex items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-white py-24 text-center text-sm text-gray-400">
              Select a property to manage its AxisRooms connection.
            </div>
          )}
        </div>
      </div>

      {/* Cross-property AxisRooms parity rollup (E8, SUPER_ADMIN oversight). */}
      <ParityRollupSection properties={properties ?? []} onSelect={setSelected} />
    </div>
  );
}

// ─── Sync — cross-property parity rollup (E8) ────────────────────────────────

function ParityRollupSection({
  properties,
  onSelect,
}: {
  properties: AdminChannelProperty[];
  onSelect: (propertyId: string) => void;
}) {
  const { data, isLoading, isError } = useAdminParityRollup();
  const nameFor = useMemo(() => new Map(properties.map((p) => [p.propertyId, p])), [properties]);
  const rows = data ?? [];

  return (
    <section className="mt-8" aria-label="AxisRooms parity across properties">
      <h2 className="text-lg font-semibold text-gray-900">Sync — AxisRooms parity across properties</h2>
      <p className="mt-1 text-sm text-gray-500">Oversight only — drift counts per property from the latest snapshot.</p>

      <div className="mt-4 rounded-xl border border-gray-200 bg-white overflow-hidden">
        {isLoading ? (
          <div className="flex items-center gap-2 p-4 text-sm text-gray-500"><Loader2 size={16} className="animate-spin" aria-hidden="true" /> Loading parity…</div>
        ) : isError ? (
          <p className="p-4 text-sm text-gray-500">Couldn&apos;t load parity rollup.</p>
        ) : rows.length === 0 ? (
          <p className="p-4 text-sm text-gray-400">No AxisRooms parity data yet — no property has synced.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr className="bg-gray-50">
                  {['Property', 'Connected OTAs', 'Changed', 'Unconfirmed', 'Errors', 'Last synced'].map((c) => (
                    <th key={c} scope="col" className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => {
                  const prop = nameFor.get(r.propertyId);
                  return (
                    <tr
                      key={r.propertyId}
                      tabIndex={0}
                      role="button"
                      onClick={() => onSelect(r.propertyId)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(r.propertyId); } }}
                      className="hover:bg-gray-50 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400"
                    >
                      <td className="px-4 py-2.5 text-sm text-gray-800">
                        {prop?.name ?? r.propertyId.slice(0, 8)}
                        {prop && <span className="block text-xs text-gray-400">{prop.clientName}</span>}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-gray-600 tabular-nums">{r.connectedOtas}</td>
                      <td className={cn('px-4 py-2.5 text-sm tabular-nums', r.externalChange > 0 ? 'text-blue-700 font-semibold' : 'text-gray-400')}>{r.externalChange}</td>
                      <td className={cn('px-4 py-2.5 text-sm tabular-nums', r.pushUnconfirmed > 0 ? 'text-amber-700 font-semibold' : 'text-gray-400')}>{r.pushUnconfirmed}</td>
                      <td className={cn('px-4 py-2.5 text-sm tabular-nums', r.syncErrors > 0 ? 'text-red-600 font-semibold' : 'text-gray-400')}>{r.syncErrors}</td>
                      <td className="px-4 py-2.5 text-xs text-gray-400 whitespace-nowrap">{fmtSyncedRelative(r.lastSyncedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function PropertyChannelPanel({ propertyId }: { propertyId: string }) {
  const { data, isLoading } = usePropertyChannel(propertyId);
  const createConn = useCreateChannelConnection();
  const updateConn = useUpdateChannelConnection();
  const deleteConn = useDeleteChannelConnection();
  const addMapping = useAddChannelMapping();
  const deleteMapping = useDeleteChannelMapping();
  const syncNow = useSyncChannelNow();

  const [hotelId, setHotelId] = useState('');
  const [ratePlans, setRatePlans] = useState('');
  const [poolSize, setPoolSize] = useState('');
  // mapping form
  const [unitId, setUnitId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [rooms, setRooms] = useState('1');
  const [error, setError] = useState('');

  if (isLoading || !data) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-500">
        <Loader2 size={16} className="animate-spin" aria-hidden="true" /> Loading…
      </div>
    );
  }

  const conn = data.connection;

  async function handleCreate() {
    setError('');
    if (!hotelId.trim()) { setError('Hotel ID is required.'); return; }
    const pool = Number.parseInt(poolSize, 10);
    try {
      await createConn.mutateAsync({
        propertyId,
        channel: 'axis_rooms',
        externalPropertyId: hotelId.trim(),
        credentials: { hotelId: hotelId.trim(), ratePlanIds: parseRatePlanIds(ratePlans) },
        ...(Number.isFinite(pool) && pool >= 1 ? { poolSize: pool } : {}),
      });
      setHotelId(''); setRatePlans(''); setPoolSize('');
    } catch { setError('Could not create the connection.'); }
  }

  async function handleSaveConn() {
    if (!conn) return;
    setError('');
    const pool = Number.parseInt(poolSize, 10);
    try {
      await updateConn.mutateAsync({
        id: conn.id,
        data: {
          externalPropertyId: hotelId.trim() || conn.hotelId,
          credentials: { hotelId: hotelId.trim() || conn.hotelId, ratePlanIds: ratePlans ? parseRatePlanIds(ratePlans) : conn.ratePlanIds },
          poolSize: poolSize === '' ? null : (Number.isFinite(pool) && pool >= 1 ? pool : conn.poolSize),
        },
      });
    } catch { setError('Could not save the connection.'); }
  }

  async function handleAddMapping() {
    if (!conn || !unitId || !roomId.trim()) return;
    setError('');
    const parsed = Number.parseInt(rooms, 10);
    try {
      await addMapping.mutateAsync({
        connectionId: conn.id,
        data: { unitId, externalListingId: roomId.trim(), allotment: Number.isFinite(parsed) && parsed >= 1 ? parsed : 1 },
      });
      setUnitId(''); setRoomId(''); setRooms('1');
    } catch { setError('Could not add the mapping (unit may already be mapped).'); }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{data.property.name}</h2>
          <p className="text-xs text-gray-400">{data.property.clientName}</p>
        </div>
        {conn && (
          <div className="flex items-center gap-3">
            {data.lastSync && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                {data.lastSync.status === 'success' ? (
                  <CheckCircle2 size={14} className="text-emerald-500" aria-hidden="true" />
                ) : (
                  <XCircle size={14} className="text-red-500" aria-hidden="true" />
                )}
                last sync: {data.lastSync.status}
              </span>
            )}
            <button
              type="button"
              onClick={() => syncNow.mutate(conn.id)}
              disabled={syncNow.isPending || data.mappings.length === 0}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium',
                syncNow.isPending || data.mappings.length === 0
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700',
              )}
            >
              <RefreshCw size={14} className={syncNow.isPending ? 'animate-spin' : ''} aria-hidden="true" />
              {syncNow.isPending ? 'Syncing…' : 'Sync Now'}
            </button>
          </div>
        )}
      </div>

      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
      {syncNow.data && (
        <p className="text-sm text-emerald-700" role="status">
          Synced {syncNow.data.syncedUnits} room type(s) — {syncNow.data.allOk ? 'all OK' : 'some failed, check logs'}.
        </p>
      )}

      {/* Connection */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
        <h3 className="text-sm font-semibold text-gray-800">AxisRooms connection</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Hotel ID</label>
            <input type="text" value={hotelId} onChange={(e) => setHotelId(e.target.value)} placeholder={conn?.hotelId || 'e.g. 234'} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Rate Plan IDs</label>
            <input type="text" value={ratePlans} onChange={(e) => setRatePlans(e.target.value)} placeholder={conn?.ratePlanIds.join(', ') || 'e.g. 1, 2'} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Shared pool size <span className="text-gray-400 font-normal">(optional)</span></label>
            <input type="number" min={1} value={poolSize} onChange={(e) => setPoolSize(e.target.value)} placeholder={conn?.poolSize != null ? String(conn.poolSize) : 'blank = independent'} className={inputCls} />
          </div>
        </div>
        <p className="text-xs text-gray-400">
          Pool size = total physical rooms, only if room types share one pool. Blank = each type independent.
        </p>
        <div className="flex items-center gap-3">
          {conn ? (
            <>
              <button type="button" onClick={handleSaveConn} disabled={updateConn.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
                {updateConn.isPending ? 'Saving…' : 'Save changes'}
              </button>
              <button type="button" onClick={() => { if (conn) deleteConn.mutate(conn.id); }} disabled={deleteConn.isPending}
                className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700">
                <Trash2 size={14} aria-hidden="true" /> Delete connection
              </button>
            </>
          ) : (
            <button type="button" onClick={handleCreate} disabled={createConn.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
              {createConn.isPending ? 'Creating…' : 'Create connection'}
            </button>
          )}
        </div>
      </div>

      {/* Mappings */}
      {conn && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
          <h3 className="text-sm font-semibold text-gray-800">Room mappings</h3>
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Unit</label>
              <select value={unitId} onChange={(e) => setUnitId(e.target.value)} className={inputCls}>
                <option value="">Select unit…</option>
                {data.units.map((u) => (<option key={u.id} value={u.id}>{u.name}</option>))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">AxisRooms Room ID</label>
              <input type="text" value={roomId} onChange={(e) => setRoomId(e.target.value)} placeholder="e.g. 3" className={inputCls} />
            </div>
            <div className="w-24">
              <label className="block text-xs font-medium text-gray-600 mb-1">Rooms</label>
              <input type="number" min={1} value={rooms} onChange={(e) => setRooms(e.target.value)} placeholder="e.g. 10" className={inputCls} />
            </div>
            <button type="button" onClick={handleAddMapping} disabled={!unitId || !roomId.trim() || addMapping.isPending}
              className={cn('rounded-lg px-4 py-2 text-sm font-medium text-white', !unitId || !roomId.trim() || addMapping.isPending ? 'bg-gray-300 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700')}>
              {addMapping.isPending ? 'Adding…' : 'Add'}
            </button>
          </div>
          <p className="text-xs text-gray-400"><strong>Rooms</strong> = how many rooms of this type (real count, e.g. 10). Blank = 1.</p>

          {data.mappings.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Unit</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Room ID</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Rooms</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.mappings.map((m) => (
                  <tr key={m.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm text-gray-800">{m.unitName}</td>
                    <td className="px-4 py-2 text-sm text-gray-600 font-mono">{m.externalListingId}</td>
                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{m.allotment}</td>
                    <td className="px-4 py-2 text-right">
                      <button type="button" onClick={() => deleteMapping.mutate({ connectionId: conn.id, mappingId: m.id })} disabled={deleteMapping.isPending}
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700" aria-label={`Remove mapping for ${m.unitName}`}>
                        <Trash2 size={13} aria-hidden="true" /> Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-gray-400">No mappings yet. Add one above.</p>
          )}
        </div>
      )}
    </div>
  );
}
